/**
 * Derive a clean "Document Name" segment from a raw filename or title.
 *
 * Strips:
 *   - the file extension (case-insensitive match against the provided one),
 *   - the facility name (treating any run of non-alphanumeric chars as a
 *     separator on both sides), and
 *   - ISO date stamps (`YYYY-MM-DD`) when `reportingYear` is known —
 *     they'd be redundant with `RY{year}` in the title.
 *
 * Then collapses runs of whitespace/underscores down to single underscores
 * and trims leading/trailing separators.
 *
 * Returns an empty string when the input is entirely facility/date noise,
 * so callers (or the per-tenant override) can fall back to a default like
 * `prettyPrintDocumentType`.
 */
export function deriveDocumentName({
  source,
  fileExtension,
  facilityName,
  reportingYear,
}: {
  source: string;
  fileExtension: string;
  facilityName: string;
  reportingYear: number | null;
}): string {
  let working = stripExtension(source, fileExtension);

  if (facilityName.trim()) {
    const words = facilityName.split(/[^a-zA-Z0-9]+/).filter(Boolean);
    if (words.length > 0) {
      const escapedWords = words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
      const pattern = escapedWords.join("[^a-zA-Z0-9]+");
      working = working.replace(new RegExp(pattern, "gi"), "");
    }
  }

  if (reportingYear !== null) {
    working = working.replace(/\d{4}-\d{2}-\d{2}/g, "");
  }

  return collapseSeparators(working);
}

function stripExtension(title: string, fileExtension: string): string {
  if (!fileExtension) return title;
  const suffix = `.${fileExtension}`;
  return title.toLowerCase().endsWith(suffix.toLowerCase())
    ? title.slice(0, title.length - suffix.length)
    : title;
}

function collapseSeparators(s: string): string {
  return s.replace(/[\s_]+/g, "_").replace(/^[_-]+|[_-]+$/g, "");
}
